import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Typography } from '@mui/material';
import { LinkedAccountingRequest } from '../../Hooks/useLinkedAccounting';
import { getAccountingSource } from '../../Utils/linkedAccounting';
import { getIncidentDayBadgeText } from '../../Utils/personHelpers';

interface Props {
  request: LinkedAccountingRequest | null;
  disabled: boolean;
  onConfirm: (selectedPersonIds: string[]) => void;
  onCancel: () => void;
  onRetry: () => void;
}

const LinkedAccountingDialog: React.FC<Props> = ({ request, disabled, onConfirm, onCancel, onRetry }) => {
  const [selected, setSelected] = useState<string[]>([]);
  useEffect(() => setSelected([]), [request]);
  return (
    <Dialog open={!!request} onClose={onCancel} fullWidth maxWidth="sm" aria-labelledby="linked-accounting-title">
      <DialogTitle id="linked-accounting-title">Account for matching records</DialogTitle>
      <DialogContent>
        {request?.loading && <Box role="status" sx={{ display: 'flex', alignItems: 'center', gap: 2 }}><CircularProgress size={24} /><Typography>Checking the complete incident roster…</Typography></Box>}
        {request?.error && <Alert severity="warning">{request.error}</Alert>}
        {request?.person && <>
          <Typography sx={{ mb: 1 }}>Mark {request.person.displayName} Accounted. Another badge or Lobby Track record shares this email. Select any additional records you have also accounted for.</Typography>
          {request.matches.map((person) => <Box key={person.personId} sx={{ mb: 1 }}>
            <FormControlLabel
              control={<Checkbox checked={selected.includes(person.personId)} disabled={disabled} onChange={(_, checked) => setSelected((prev) => checked ? [...prev, person.personId] : prev.filter((id) => id !== person.personId))} />}
              label={`Also account for ${person.displayName} (${getAccountingSource(person) === 'employeeBadge' ? 'Employee badge' : 'Lobby Track'})`}
            />
            <Typography variant="body2" sx={{ ml: 4, color: 'text.secondary' }}>{getIncidentDayBadgeText(person)}</Typography>
          </Box>)}
        </>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        {request?.error ? <Button onClick={onRetry} disabled={disabled}>Retry</Button> : <Button variant="contained" onClick={() => onConfirm(selected)} disabled={disabled || !request?.person || request.loading}>Mark Accounted</Button>}
      </DialogActions>
    </Dialog>
  );
};

export default LinkedAccountingDialog;
